import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Stack,
  Divider,
  alpha,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { useForm, useFieldArray } from "react-hook-form";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

interface EditDialogProps {
  open: boolean;
  sectionName: string | null;
  jsonData: Record<string, string[]>;
  onClose: () => void;
  onSave: (updatedData: Record<string, string[]>) => void;
}

interface FormEntry {
  localKey: string;
  localValue: string; // We'll store the comma-separated string here
}

interface FormData {
  entries: FormEntry[];
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#bb0000",
      light: alpha("#bb0000", 0.9),
      dark: "#990000",
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "8px 24px",
          borderRadius: 6,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 6,
            backgroundColor: "#f8f9fa",
            "&:hover": {
              backgroundColor: "#f1f3f5",
            },
            "&.Mui-focused": {
              backgroundColor: "#ffffff",
            },
          },
        },
      },
    },
  },
});

const EditDialog: React.FC<EditDialogProps> = ({
  open,
  sectionName,
  jsonData,
  onClose,
  onSave,
}) => {
  /**
   * Set up React Hook Form
   */
  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: {
      entries: [],
    },
  });

  /**
   * FieldArray to manage the array of entries
   */
  const { fields, append, remove } = useFieldArray<FormData>({
    control,
    name: "entries",
  });

  /**
   * When the dialog opens, initialize form data from `jsonData`
   */
  useEffect(() => {
    if (open) {
      // Convert `jsonData` into an array of form entries
      const formEntries = Object.entries(jsonData).map(([key, value]) => ({
        localKey: key,
        localValue: value.join(", "), // join into a comma-separated string
      }));

      reset({
        entries: formEntries,
      });
    }
  }, [open, jsonData, reset]);

  /**
   * Handler to add a new empty entry (let user type the key/value)
   */
  const handleAddEntry = () => {
    append({ localKey: "", localValue: "" });
  };

  /**
   * Handler to remove an entry
   */
  const handleDeleteEntry = (index: number) => {
    remove(index);
  };

  /**
   * On form submit, parse comma-separated values -> arrays
   * then call `onSave`
   */
  const onSubmit = (data: FormData) => {
    const updatedData = data.entries.reduce<Record<string, string[]>>(
      (acc, entry) => {
        // Parse the localValue into an array
        const valuesArray = entry.localValue
          .split(",")
          .map((val) => val.trim())
          .filter(Boolean);

        // Only store if the key is not empty
        if (entry.localKey.trim()) {
          acc[entry.localKey.trim()] = valuesArray;
        }
        return acc;
      },
      {}
    );

    onSave(updatedData);
    onClose();
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          elevation: 4,
          sx: { minHeight: "50vh" },
        }}
      >
        <DialogTitle
          sx={{
            pt: 3,
            px: 3,
            fontSize: "1.5rem",
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          {sectionName}
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {fields.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "200px",
                color: "text.secondary",
              }}
            >
              <Typography variant="body1">No entries available</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddEntry}
                sx={{ mt: 2 }}
              >
                Add First Entry
              </Button>
            </Box>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={4}>
                {fields.map((field, index) => (
                  <Box key={field.id}>
                    {/* Row: Key + Delete button */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <TextField
                        label="Key"
                        variant="outlined"
                        size="small"
                        {...control.register(
                          `entries.${index}.localKey` as const
                        )}
                        sx={{
                          width: "75%",
                          "& .MuiOutlinedInput-root": {
                            fontSize: "0.95rem",
                          },
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntry(index)}
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            color: "error.main",
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              0.08
                            ),
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Row: Value */}
                    <TextField
                      fullWidth
                      label="Comma-separated values"
                      variant="outlined"
                      size="small"
                      {...control.register(
                        `entries.${index}.localValue` as const
                      )}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.95rem",
                        },
                      }}
                    />

                    {index < fields.length - 1 && <Divider sx={{ mt: 4 }} />}
                  </Box>
                ))}
              </Stack>
            </form>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              color: "text.primary",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            Cancel
          </Button>
          {fields.length > 0 && (
            <Button
              variant="contained"
              type="submit"
              form={/* the ID of the form, if needed */ undefined}
              onClick={handleSubmit(onSubmit)}
              sx={{
                px: 4,
                backgroundColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            >
              Save Changes
            </Button>
          )}
          {fields.length > 0 && (
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddEntry}
              sx={{
                ml: 2,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Add Entry
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default EditDialog;
